import { useEffect, useState } from "react";
import { usePageNameContext } from "../../contexts/PageTitleContext";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { BeakerIcon, MapIcon, PlusSmallIcon } from "@heroicons/react/24/outline";
import PostcodeCoverageMap from "../../components/postcodeGroups/PostcodeCoverageMap";
import usePostcodeGroups from "../../data/usePostcodeGroups";
import usePostcodePriceMutations from "../../data/usePostcodePriceMutations";
import { IPostcodeGroup, IPostcodeGroupsResponse } from "../../models/PostcodeGroup";
import PostcodesGroupTable from "../../components/postcodeGroups/PostcodeGroupTable";
import PostcodeGroupsModal from "../../components/postcodeGroups/PostcodeGroupModal";
import PostcodePriceTestModal from "../../components/postcodeGroups/PostcodePriceTestModal";
import ImportFromCsv from "../../components/shared/ImportFromCsv";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { NumberInput } from "../../components/shared/Inputs/NumberInput";
import ScrapDropOff from "../../components/scrapPrices/ScrapDropOff";

export default function ScrapPrice() {
  const { setInfo } = usePageNameContext();
  // const { scrapPrices: allScrapPrices, create, update } = useScrapPrices();
  const { postcodeGroups: allPostcodeGroups, create, update, createBulk } = usePostcodeGroups();
  const { createBulk: createPostcodePriceBulk } = usePostcodePriceMutations();
  const [postcodeGroups, setPostcodeGroups] = useState<IPostcodeGroup[] | undefined>(undefined);
  const [group, setGroup] = useState<IPostcodeGroup | undefined>(undefined);
  const [editGroup, setEditGroup] = useState<IPostcodeGroup | undefined>(undefined);

  const [csvOpen, setCsvOpen] = useState(false);

  const { saveHandlers } = useDefaultCRUDHandlers("Postcode");

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    setInfo({
      name: "Postcode Prices",
      desc: "Catalogue prices against outward postcodes",
    });
  }, []);

  useEffect(() => {
    if (!allPostcodeGroups.isLoading && allPostcodeGroups.data) {

      // Filtering code goes here

      setPostcodeGroups(allPostcodeGroups.data?.data);
      setIsLoading(false);
    }
  }, [allPostcodeGroups.isLoading, allPostcodeGroups.data, searchTerm]);

  const handleSetPostcodeGroupPrice = (postcodeGroup: IPostcodeGroup) => {
    setEditGroup(postcodeGroup);
    setCreateOpen(true);
  };

  const handleAddPostcodeGroup = () => {
    setEditGroup(undefined);
    setCreateOpen(true);
  }

  const handleBulkImport = (postcodeGroups: IPostcodeGroup[]) => {
    // Array of unique “group” entries: { name: "Bath", postcode: "BA", basePrice: number }
    const uniqueGroups: any[] = []
    // Array of individual postcodes: { postcode: "BA1", price: number }
    const individualPostcodes: { prefix: string, postcode: string; price: number; postcodeGroupId?: string }[] = []
    // Keep track of which prefixes we've already seen
    const seenPrefixes = new Set<string>()

    for (const pg of postcodeGroups) {
      // pg.name is like "BA - BATH"
      const [prefix, rawName] = pg.name.split(" - ")
      // choose whichever numeric field holds your “value” (here I use basePrice)
      const price = pg.basePrice

      // 1) build unique group list
      if (!seenPrefixes.has(prefix)) {
        seenPrefixes.add(prefix)
        // Title‐case the location name (from "BATH" → "Bath")
        const name = rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase()

        uniqueGroups.push({ name: name, postcode: prefix, basePrice: price })
      }

      // 2) build individual postcode list
      // pg.postcode is like "BA1"
      individualPostcodes.push({ prefix, postcode: pg.postcode, price })
    }

    // now uniqueGroups & individualPostcodes are populated
    console.log({ uniqueGroups, individualPostcodes })

    createBulk.mutate(uniqueGroups, {
      onSuccess: (data: IPostcodeGroupsResponse) => {
        for(let postcode in individualPostcodes) individualPostcodes[postcode].postcodeGroupId = data.data.find(pg => pg.postcode === individualPostcodes[postcode].prefix)?.id;

        createPostcodePriceBulk.mutate(individualPostcodes, {
          onSuccess: () => {
            saveHandlers.onSuccess();
            setCsvOpen(false);
          },
          onError: (error) => {
            console.error("Bulk import failed:", error);
            saveHandlers.onError();
          }
        });
      },
      onError: (error) => {
        console.error("Bulk import failed:", error);
        saveHandlers.onError();
      }
    });
  }

  const prototype: any = {
    name: "",
    postcode: "",
    basePrice: 0,
    onlyGoodEngineCondition: false,
    onlyGoodBodyCondition: false,
    thresholdPrice: 0,
  };

  return allPostcodeGroups.isLoading || isLoading ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : (
    <>
      <PostcodeGroupsModal open={createOpen} setOpen={setCreateOpen} postcodeGroup={editGroup} />
      <PostcodePriceTestModal open={testOpen} setOpen={setTestOpen} />

      {/* ------------------- */}
      <ScrapDropOff />
      {/* ------------------- */}

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">

        <div className="flex flex-wrap gap-2">
          <button
            className=" bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
            type="button"
            onClick={() => handleAddPostcodeGroup()}
          >
            <PlusSmallIcon className="h-5 w-5 mr-2" />
            Add New Postcode Group
          </button>

          <button
            className="bg-white hover:bg-gray-50 inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm"
            type="button"
            onClick={() => setTestOpen(true)}
          >
            <BeakerIcon className="h-5 w-5 mr-2" />
            Check a Reg &amp; Postcode
          </button>

          <button
            className={`${mapOpen ? "bg-primary-50 border-primary-300 text-primary-700" : "bg-white hover:bg-gray-50 border-gray-300 text-gray-700"} inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium leading-4 shadow-sm`}
            type="button"
            onClick={() => setMapOpen((o) => !o)}
          >
            <MapIcon className="h-5 w-5 mr-2" />
            {mapOpen ? "Hide Coverage Map" : "Coverage Map"}
          </button>
        </div>

        <ImportFromCsv<IPostcodeGroup> prototypeObject={prototype} onSuccess={handleBulkImport} open={csvOpen} setOpen={setCsvOpen} />
      </div>

      {/* Map picker: click districts to cover / exclude / price them, or add a postcode + radius */}
      {mapOpen && (
        <div className="mt-4 rounded-lg bg-white p-4 shadow">
          <PostcodeCoverageMap postcodeGroups={postcodeGroups ?? []} />
        </div>
      )}

      {/* Table of postcode groups */}
      <PostcodesGroupTable postcodeGroups={postcodeGroups!} setPostcodeGroupPrice={handleSetPostcodeGroupPrice} />
    </>
  );
}
